import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FilePlus2,
  Plus,
  Trash2,
  Edit2,
  RefreshCw,
  FileText,
  CheckSquare,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Copy,
  ShieldAlert,
  Headphones,
  AlertTriangle,
  Scale,
  Tag,
  GripVertical,
  X
} from 'lucide-react';

const TIPOS_CASO = [
  { value: 'FRAUDE', label: 'Fraude', color: '#ef4444', icon: ShieldAlert },
  { value: 'SOPORTE', label: 'Soporte', color: '#3b82f6', icon: Headphones },
  { value: 'ALERTA', label: 'Alerta', color: '#f97316', icon: AlertTriangle },
  { value: 'COMPLIANCE', label: 'Compliance', color: '#8b5cf6', icon: Scale },
  { value: 'OTRO', label: 'Otro', color: '#64748b', icon: Tag },
];

const PRIORIDADES = [
  { value: 'BAJA', label: 'Baja', color: '#22c55e' },
  { value: 'MEDIA', label: 'Media', color: '#eab308' },
  { value: 'ALTA', label: 'Alta', color: '#f97316' },
  { value: 'CRITICA', label: 'Crítica', color: '#ef4444' },
];

const PlantillasCaso = () => {
  const [templates, setTemplates] = useState([]);
  const [tiposCaso, setTiposCaso] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Form
  const [newTemplate, setNewTemplate] = useState({
    nombre: '',
    descripcion: '',
    tipo_caso: 'FRAUDE',
    documentos_requeridos: [],
    pasos_investigacion: [],
    prioridad_default: 'MEDIA',
    tags_default: []
  });
  const [editTemplate, setEditTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Temp items for adding
  const [newDocumento, setNewDocumento] = useState({ nombre: '', descripcion: '', obligatorio: true });
  const [newPaso, setNewPaso] = useState({ titulo: '', descripcion: '', obligatorio: true });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    loadTemplates();
    loadTiposCaso();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/case-templates/');
      setTemplates(res.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadTiposCaso = async () => {
    try {
      const res = await api.get('/cases/config/tipos');
      setTiposCaso(res.data);
    } catch (error) {
      // Usar tipos por defecto
    }
  };

  const getTipoInfo = (codigo) => {
    const custom = tiposCaso.find(t => t.codigo === codigo);
    if (custom) return custom;
    return TIPOS_CASO.find(t => t.value === codigo) || TIPOS_CASO[4];
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/case-templates/', newTemplate);
      toast.success('Plantilla creada correctamente');
      setShowCreateDialog(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editTemplate?.nombre?.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/case-templates/${editTemplate.id}`, {
        nombre: editTemplate.nombre,
        descripcion: editTemplate.descripcion,
        tipo_caso: editTemplate.tipo_caso,
        documentos_requeridos: editTemplate.documentos_requeridos,
        pasos_investigacion: editTemplate.pasos_investigacion,
        prioridad_default: editTemplate.prioridad_default,
        tags_default: editTemplate.tags_default
      });
      toast.success('Plantilla actualizada');
      setShowEditDialog(false);
      setEditTemplate(null);
      loadTemplates();
      if (selectedTemplate?.id === editTemplate.id) {
        setSelectedTemplate(editTemplate);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    
    try {
      await api.delete(`/case-templates/${templateId}`);
      toast.success('Plantilla eliminada');
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
      loadTemplates();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setNewTemplate({
      nombre: '',
      descripcion: '',
      tipo_caso: 'FRAUDE',
      documentos_requeridos: [],
      pasos_investigacion: [],
      prioridad_default: 'MEDIA',
      tags_default: []
    });
    setNewDocumento({ nombre: '', descripcion: '', obligatorio: true });
    setNewPaso({ titulo: '', descripcion: '', obligatorio: true });
    setNewTag('');
  };

  // Handlers para documentos
  const addDocumento = (isEdit = false) => {
    if (!newDocumento.nombre.trim()) return;
    
    if (isEdit && editTemplate) {
      setEditTemplate({
        ...editTemplate,
        documentos_requeridos: [...editTemplate.documentos_requeridos, { ...newDocumento }]
      });
    } else {
      setNewTemplate({
        ...newTemplate,
        documentos_requeridos: [...newTemplate.documentos_requeridos, { ...newDocumento }]
      });
    }
    setNewDocumento({ nombre: '', descripcion: '', obligatorio: true });
  };

  const removeDocumento = (index, isEdit = false) => {
    if (isEdit && editTemplate) {
      setEditTemplate({
        ...editTemplate,
        documentos_requeridos: editTemplate.documentos_requeridos.filter((_, i) => i !== index)
      });
    } else {
      setNewTemplate({
        ...newTemplate,
        documentos_requeridos: newTemplate.documentos_requeridos.filter((_, i) => i !== index)
      });
    }
  };

  // Handlers para pasos
  const addPaso = (isEdit = false) => {
    if (!newPaso.titulo.trim()) return;
    
    const currentPasos = isEdit ? editTemplate.pasos_investigacion : newTemplate.pasos_investigacion;
    const paso = { ...newPaso, orden: currentPasos.length + 1 };
    
    if (isEdit && editTemplate) {
      setEditTemplate({
        ...editTemplate,
        pasos_investigacion: [...editTemplate.pasos_investigacion, paso]
      });
    } else {
      setNewTemplate({
        ...newTemplate,
        pasos_investigacion: [...newTemplate.pasos_investigacion, paso]
      });
    }
    setNewPaso({ titulo: '', descripcion: '', obligatorio: true });
  };

  const removePaso = (index, isEdit = false) => {
    if (isEdit && editTemplate) {
      const updated = editTemplate.pasos_investigacion.filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, orden: i + 1 }));
      setEditTemplate({ ...editTemplate, pasos_investigacion: updated });
    } else {
      const updated = newTemplate.pasos_investigacion.filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, orden: i + 1 }));
      setNewTemplate({ ...newTemplate, pasos_investigacion: updated });
    }
  };

  const movePaso = (index, direction, isEdit = false) => {
    const pasos = isEdit ? [...editTemplate.pasos_investigacion] : [...newTemplate.pasos_investigacion];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= pasos.length) return;
    
    [pasos[index], pasos[newIndex]] = [pasos[newIndex], pasos[index]];
    pasos.forEach((p, i) => p.orden = i + 1);
    
    if (isEdit) {
      setEditTemplate({ ...editTemplate, pasos_investigacion: pasos });
    } else {
      setNewTemplate({ ...newTemplate, pasos_investigacion: pasos });
    }
  };

  // Handlers para tags
  const addTag = (isEdit = false) => {
    if (!newTag.trim()) return;
    
    if (isEdit && editTemplate) {
      if (!editTemplate.tags_default.includes(newTag.trim())) {
        setEditTemplate({
          ...editTemplate,
          tags_default: [...editTemplate.tags_default, newTag.trim()]
        });
      }
    } else {
      if (!newTemplate.tags_default.includes(newTag.trim())) {
        setNewTemplate({
          ...newTemplate,
          tags_default: [...newTemplate.tags_default, newTag.trim()]
        });
      }
    }
    setNewTag('');
  };

  const removeTag = (tag, isEdit = false) => {
    if (isEdit && editTemplate) {
      setEditTemplate({
        ...editTemplate,
        tags_default: editTemplate.tags_default.filter(t => t !== tag)
      });
    } else {
      setNewTemplate({
        ...newTemplate,
        tags_default: newTemplate.tags_default.filter(t => t !== tag)
      });
    }
  };

  // Render del formulario de plantilla
  const renderTemplateForm = (template, setTemplate, isEdit = false) => {
    return (
      <div className="space-y-6 py-4 max-h-[70vh] overflow-y-auto pr-2">
        {/* Info Básica */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Nombre de la Plantilla *</Label>
            <Input
              value={template.nombre}
              onChange={(e) => setTemplate({ ...template, nombre: e.target.value })}
              placeholder="Ej: Investigación Fraude Estándar"
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          <div className="col-span-2">
            <Label>Descripción</Label>
            <Textarea
              value={template.descripcion || ''}
              onChange={(e) => setTemplate({ ...template, descripcion: e.target.value })}
              placeholder="Describe cuándo usar esta plantilla..."
              rows={2}
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          <div>
            <Label>Tipo de Caso</Label>
            <Select 
              value={template.tipo_caso} 
              onValueChange={(v) => setTemplate({ ...template, tipo_caso: v })}
            >
              <SelectTrigger style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_CASO.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Prioridad por Defecto</Label>
            <Select 
              value={template.prioridad_default} 
              onValueChange={(v) => setTemplate({ ...template, prioridad_default: v })}
            >
              <SelectTrigger style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORIDADES.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Documentos Requeridos */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#0369a1' }}>
            <FileText size={16} />
            Documentos Requeridos ({template.documentos_requeridos?.length || 0})
          </h4>
          
          {template.documentos_requeridos?.length > 0 && (
            <div className="space-y-2 mb-3">
              {template.documentos_requeridos.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-slate-400" />
                    <span className="text-sm font-medium">{doc.nombre}</span>
                    {doc.obligatorio && (
                      <Badge className="text-xs" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                        Obligatorio
                      </Badge>
                    )}
                  </div>
                  <button onClick={() => removeDocumento(idx, isEdit)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="grid grid-cols-12 gap-2">
            <Input
              className="col-span-5"
              placeholder="Nombre del documento"
              value={newDocumento.nombre}
              onChange={(e) => setNewDocumento({ ...newDocumento, nombre: e.target.value })}
              style={{ backgroundColor: '#fff' }}
            />
            <Input
              className="col-span-4"
              placeholder="Descripción (opcional)"
              value={newDocumento.descripcion}
              onChange={(e) => setNewDocumento({ ...newDocumento, descripcion: e.target.value })}
              style={{ backgroundColor: '#fff' }}
            />
            <label className="col-span-2 flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={newDocumento.obligatorio}
                onChange={(e) => setNewDocumento({ ...newDocumento, obligatorio: e.target.checked })}
              />
              Oblig.
            </label>
            <button
              onClick={() => addDocumento(isEdit)}
              className="col-span-1 p-2 rounded bg-blue-500 text-white hover:bg-blue-600"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Pasos de Investigación */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#166534' }}>
            <CheckSquare size={16} />
            Pasos de Investigación ({template.pasos_investigacion?.length || 0})
          </h4>
          
          {template.pasos_investigacion?.length > 0 && (
            <div className="space-y-2 mb-3">
              {template.pasos_investigacion.map((paso, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-0.5">
                      <button 
                        onClick={() => movePaso(idx, 'up', isEdit)}
                        disabled={idx === 0}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button 
                        onClick={() => movePaso(idx, 'down', isEdit)}
                        disabled={idx === template.pasos_investigacion.length - 1}
                        className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">
                      {paso.orden}
                    </span>
                    <span className="text-sm font-medium">{paso.titulo}</span>
                    {paso.obligatorio && (
                      <Badge className="text-xs" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                        Obligatorio
                      </Badge>
                    )}
                  </div>
                  <button onClick={() => removePaso(idx, isEdit)} className="text-red-500 hover:text-red-700">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="grid grid-cols-12 gap-2">
            <Input
              className="col-span-5"
              placeholder="Título del paso"
              value={newPaso.titulo}
              onChange={(e) => setNewPaso({ ...newPaso, titulo: e.target.value })}
              style={{ backgroundColor: '#fff' }}
            />
            <Input
              className="col-span-4"
              placeholder="Descripción (opcional)"
              value={newPaso.descripcion}
              onChange={(e) => setNewPaso({ ...newPaso, descripcion: e.target.value })}
              style={{ backgroundColor: '#fff' }}
            />
            <label className="col-span-2 flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={newPaso.obligatorio}
                onChange={(e) => setNewPaso({ ...newPaso, obligatorio: e.target.checked })}
              />
              Oblig.
            </label>
            <button
              onClick={() => addPaso(isEdit)}
              className="col-span-1 p-2 rounded bg-green-500 text-white hover:bg-green-600"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Tags por Defecto */}
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#fdf4ff', border: '1px solid #f5d0fe' }}>
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#86198f' }}>
            <Tag size={16} />
            Tags por Defecto
          </h4>
          
          {template.tags_default?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {template.tags_default.map((tag, idx) => (
                <Badge 
                  key={idx} 
                  className="flex items-center gap-1 cursor-pointer"
                  style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}
                  onClick={() => removeTag(tag, isEdit)}
                >
                  {tag}
                  <X size={12} />
                </Badge>
              ))}
            </div>
          )}
          
          <div className="flex gap-2">
            <Input
              placeholder="Nuevo tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTag(isEdit)}
              style={{ backgroundColor: '#fff' }}
            />
            <button
              onClick={() => addTag(isEdit)}
              className="px-3 py-2 rounded bg-purple-500 text-white hover:bg-purple-600"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FilePlus2 className="text-purple-600" />
            Plantillas de Caso
          </h1>
          <p className="text-slate-600 mt-1">
            Crea plantillas con documentos y pasos predefinidos para cada tipo de caso
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white"
          style={{ backgroundColor: '#7c3aed' }}
          data-testid="create-template-btn"
        >
          <Plus size={18} />
          Nueva Plantilla
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Plantillas */}
        <div className="lg:col-span-1">
          <Card className="p-4 bg-white">
            <h2 className="font-semibold text-slate-900 mb-4">Plantillas ({templates.length})</h2>
            <div className="space-y-2">
              {templates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">
                  No hay plantillas creadas
                </p>
              ) : (
                templates.map(template => {
                  const tipoInfo = getTipoInfo(template.tipo_caso);
                  return (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id 
                          ? 'ring-2 ring-purple-500' 
                          : 'hover:bg-slate-50'
                      }`}
                      style={{ 
                        backgroundColor: selectedTemplate?.id === template.id ? '#faf5ff' : undefined,
                        borderLeft: `4px solid ${tipoInfo.color || '#64748b'}`
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{template.nombre}</span>
                        <ChevronRight size={16} className="text-slate-400" />
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          className="text-xs"
                          style={{ backgroundColor: `${tipoInfo.color}20`, color: tipoInfo.color }}
                        >
                          {tipoInfo.nombre || tipoInfo.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {template.documentos_requeridos?.length || 0} docs • {template.pasos_investigacion?.length || 0} pasos
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        {/* Detalle de Plantilla */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <Card className="p-6 bg-white">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{selectedTemplate.nombre}</h2>
                  <p className="text-sm text-slate-500 mt-1">{selectedTemplate.descripcion || 'Sin descripción'}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge style={{ backgroundColor: '#f0f9ff', color: '#0369a1' }}>
                      {getTipoInfo(selectedTemplate.tipo_caso).nombre || getTipoInfo(selectedTemplate.tipo_caso).label}
                    </Badge>
                    <Badge style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      Prioridad: {selectedTemplate.prioridad_default}
                    </Badge>
                    {selectedTemplate.veces_usado > 0 && (
                      <Badge variant="outline">Usado {selectedTemplate.veces_usado} veces</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { 
                      setEditTemplate({ ...selectedTemplate }); 
                      setShowEditDialog(true); 
                    }}
                    className="p-2 rounded-lg hover:bg-slate-100"
                    title="Editar plantilla"
                  >
                    <Edit2 size={18} className="text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                    className="p-2 rounded-lg hover:bg-red-50"
                    title="Eliminar plantilla"
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Documentos */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0f9ff' }}>
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#0369a1' }}>
                    <FileText size={16} />
                    Documentos Requeridos
                  </h3>
                  {selectedTemplate.documentos_requeridos?.length === 0 ? (
                    <p className="text-sm text-slate-400">No hay documentos definidos</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedTemplate.documentos_requeridos?.map((doc, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded">
                          <FileText size={14} className="text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium">{doc.nombre}</p>
                            {doc.descripcion && (
                              <p className="text-xs text-slate-500">{doc.descripcion}</p>
                            )}
                            {doc.obligatorio && (
                              <Badge className="text-xs mt-1" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                                Obligatorio
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pasos */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4' }}>
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#166534' }}>
                    <CheckSquare size={16} />
                    Pasos de Investigación
                  </h3>
                  {selectedTemplate.pasos_investigacion?.length === 0 ? (
                    <p className="text-sm text-slate-400">No hay pasos definidos</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedTemplate.pasos_investigacion?.map((paso, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded">
                          <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {paso.orden}
                          </span>
                          <div>
                            <p className="text-sm font-medium">{paso.titulo}</p>
                            {paso.descripcion && (
                              <p className="text-xs text-slate-500">{paso.descripcion}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              {selectedTemplate.tags_default?.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-2">Tags por Defecto</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.tags_default.map((tag, idx) => (
                      <Badge key={idx} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-6 bg-white flex flex-col items-center justify-center min-h-[400px] text-slate-400">
              <FilePlus2 size={48} className="mb-4 opacity-50" />
              <p>Selecciona una plantilla para ver sus detalles</p>
            </Card>
          )}
        </div>
      </div>

      {/* Dialog: Crear Plantilla */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="text-purple-600" />
              Nueva Plantilla de Caso
            </DialogTitle>
          </DialogHeader>
          
          {renderTemplateForm(newTemplate, setNewTemplate, false)}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleCreateTemplate}
              disabled={saving}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              {saving ? 'Creando...' : 'Crear Plantilla'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Plantilla */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-purple-600" />
              Editar Plantilla
            </DialogTitle>
          </DialogHeader>
          
          {editTemplate && renderTemplateForm(editTemplate, setEditTemplate, true)}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleUpdateTemplate}
              disabled={saving}
              className="px-4 py-2 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlantillasCaso;
